import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Images, ChevronDown, X, CalendarDays, ShieldCheck } from "lucide-react";
import AppShell, { useAppUser } from "@/components/AppShell";
import { authApi, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] outline-none transition-colors duration-200";

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

export default function Galeria() {
  const { user } = useAppUser() || {};
  const [units, setUnits] = useState(null);
  const [unitId, setUnitId] = useState("");
  const [treinos, setTreinos] = useState(null);
  const [openDate, setOpenDate] = useState(null);
  const [images, setImages] = useState({});
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    if (!user) return;
    authApi().get("/units").then((r) => setUnits(r.data.units || [])).catch(() => setUnits([]));
  }, [user]);

  const pickUnit = async (uid) => {
    setUnitId(uid);
    setTreinos(null);
    setOpenDate(null);
    setImages({});
    if (!uid) return;
    try {
      const r = await authApi().get("/galeria", { params: { unit_id: uid } });
      setTreinos(r.data.treinos);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setTreinos([]);
    }
  };

  const byDate = (treinos || []).reduce((acc, t) => {
    (acc[t.date] = acc[t.date] || []).push(t);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort((a, b) => (a < b ? 1 : -1));

  const toggleDate = async (d) => {
    if (openDate === d) {
      setOpenDate(null);
      return;
    }
    setOpenDate(d);
    for (const t of byDate[d]) {
      if (images[t.id]) continue;
      try {
        const r = await authApi().get(`/galeria/${t.id}/imagem`);
        setImages((prev) => ({ ...prev, [t.id]: r.data.image }));
      } catch {}
    }
  };

  return (
    <AppShell active="galeria">
      <section data-testid="galeria-page">
        <h1 className="font-display text-2xl font-bold" data-testid="galeria-title">Galeria</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-2">TREINOS REGISTRADOS EM FOTO</p>
        <p className="flex items-center gap-2 text-xs text-zinc-500 mb-6" data-testid="galeria-privacy-note">
          <ShieldCheck size={14} className="text-gold shrink-0" /> Rostos de usuários com censura ativa no cadastro (obrigatória para menores de 18) aparecem borrados; rostos não identificados são borrados quando a chamada usa "Censurar desconhecidos: sim".
        </p>

        <select value={unitId} onChange={(e) => pickUnit(e.target.value)} className={inputCls} data-testid="galeria-unit-select">
          <option value="">Selecione a unidade...</option>
          {(units || []).map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>

        {unitId && treinos === null && <p className="text-zinc-500 text-sm mt-6">Carregando registros...</p>}

        {unitId && treinos !== null && dates.length === 0 && (
          <div className="flex flex-col items-center gap-4 border border-dashed border-white/15 rounded-2xl py-16 text-zinc-500 mt-6" data-testid="galeria-empty">
            <Images size={30} />
            <p className="text-sm">Nenhum treino fotografado nesta unidade ainda. Use a Chamada por Foto no menu Presença.</p>
          </div>
        )}

        <div className="mt-6 space-y-3">
          {dates.map((d, gi) => (
            <div key={d} className="bg-[#1C1C23] border border-white/10 rounded-2xl overflow-hidden" data-testid={`galeria-group-${gi}`}>
              <button
                type="button"
                onClick={() => toggleDate(d)}
                data-testid={`galeria-date-${gi}`}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.03] transition-colors"
              >
                <span className="flex items-center gap-3">
                  <CalendarDays size={16} className="text-gold" />
                  <span className="font-display font-semibold">{fmtDate(d)}</span>
                  <span className="font-mono text-[11px] text-zinc-500">{byDate[d][0].day.toUpperCase()} · {byDate[d].length} TREINO(S)</span>
                </span>
                <ChevronDown size={16} className={`text-zinc-500 transition-transform ${openDate === d ? "rotate-180" : ""}`} />
              </button>

              {openDate === d && (
                <div className="px-6 pb-6 grid sm:grid-cols-2 gap-4">
                  {byDate[d].map((t, i) => (
                    <div key={t.id} className="border border-white/10 rounded-xl overflow-hidden" data-testid={`galeria-item-${gi}-${i}`}>
                      <button type="button" onClick={() => images[t.id] && setViewer({ src: images[t.id], t })} className="w-full" data-testid={`galeria-thumb-${gi}-${i}`}>
                        {images[t.id] ? (
                          <img src={images[t.id]} alt={`Treino ${t.class_name || ""}`} className="w-full h-52 object-cover hover:opacity-90 transition-opacity" />
                        ) : (
                          <div className="w-full h-52 bg-obsidian flex items-center justify-center text-zinc-600 text-sm">Carregando imagem...</div>
                        )}
                      </button>
                      <div className="px-4 py-3">
                        <p className="font-display font-semibold text-sm" data-testid={`galeria-item-title-${gi}-${i}`}>
                          {t.class_name || "Treino"} · {t.start}–{t.end}
                        </p>
                        <p className="font-mono text-[11px] text-zinc-500 mt-1">
                          {t.presentes ?? 0} PRESENTE(S) · POR {(t.taken_by || "").toUpperCase()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {viewer && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="galeria-viewer">
          <div className="absolute inset-0 bg-black/85" onClick={() => setViewer(null)} />
          <div className="relative max-w-4xl w-full">
            <button type="button" onClick={() => setViewer(null)} data-testid="galeria-viewer-close" className="absolute -top-2 -right-2 z-10 w-9 h-9 rounded-full bg-obsidian border border-white/20 flex items-center justify-center text-zinc-300 hover:text-gold" aria-label="Fechar imagem">
              <X size={18} />
            </button>
            <img src={viewer.src} alt="Registro do treino" className="w-full rounded-2xl border border-white/10" />
            <p className="text-center font-mono text-[11px] tracking-widest text-zinc-400 mt-3">
              {(viewer.t.class_name || "TREINO").toUpperCase()} · {fmtDate(viewer.t.date)} · {viewer.t.start}–{viewer.t.end}
            </p>
          </div>
        </div>
      )}
    </AppShell>
  );
}
